'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import AppLogo from '@/components/ui/AppLogo';
import { LayoutDashboard, FileText, Bell, Building2, Settings, ChevronLeft, ChevronRight, Globe, ShieldAlert, LogOut, HelpCircle, Shield, Zap, Anchor, Truck, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/lib/AuthContext';
import Icon from '@/components/ui/AppIcon';


interface NavItem {
  id: string;
  label: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  href: string;
  badge?: number;
  badgeVariant?: 'danger' | 'warning' | 'info';
  adminOnly?: boolean;
}

const navGroups: { label: string; items: NavItem[] }[] = [
  {
    label: 'Intelligence',
    items: [
      { id: 'nav-dashboard', label: 'Risk Dashboard', icon: LayoutDashboard, href: '/ai-risk-dashboard' },
      { id: 'nav-reports', label: 'AI Reports', icon: FileText, href: '/ai-reports' },
      { id: 'nav-alerts', label: 'Alerts', icon: Bell, href: '/alerts', badge: 6, badgeVariant: 'danger' },
    ],
  },
  {
    label: 'Risk Domains',
    items: [
      { id: 'nav-sanctions', label: 'Sanctions', icon: ShieldAlert, href: '/sanctions-tracker', badge: 2, badgeVariant: 'warning' },
      { id: 'nav-cyber', label: 'Cyber Risk', icon: Zap, href: '/cyber-risk' },
      { id: 'nav-maritime', label: 'Maritime / Logistics', icon: Anchor, href: '/maritime-risk' },
      { id: 'nav-political', label: 'Political Risk', icon: AlertTriangle, href: '/political-risk' },
      { id: 'nav-supply', label: 'Supply Chain', icon: Truck, href: '/supply-chain' },
    ],
  },
  {
    label: 'Exposure',
    items: [
      { id: 'nav-company', label: 'Company Analyser', icon: Building2, href: '/company-analyser' },
      { id: 'nav-regions', label: 'Region Monitor', icon: Globe, href: '/region-monitor' },
    ],
  },
  {
    label: 'System',
    items: [
      { id: 'nav-admin', label: 'Admin Panel', icon: Shield, href: '/admin', adminOnly: true },
      { id: 'nav-settings', label: 'Settings', icon: Settings, href: '/settings' },
      { id: 'nav-help', label: 'Help & Support', icon: HelpCircle, href: '/help' },
    ],
  },
];

const badgeClasses: Record<string, string> = {
  danger: 'bg-danger text-white',
  warning: 'bg-warning text-background',
  info: 'bg-info text-white',
};

interface SidebarProps {
  activeRoute?: string;
}

export default function Sidebar({ activeRoute }: SidebarProps) {
  const [collapsed, setCollapsed] = useState(false);
  const { user, logout, isAdmin } = useAuth();

  return (
    <aside
      className="flex flex-col bg-card border-r border-border relative z-20 flex-shrink-0"
      style={{
        width: collapsed ? '64px' : '240px',
        transition: 'width 300ms ease',
      }}
    >
      {/* Logo */}
      <div className="flex items-center h-16 px-4 border-b border-border flex-shrink-0 overflow-hidden">
        <div className="flex items-center gap-2 min-w-0">
          <AppLogo size={28} />
          {!collapsed && (
            <div className="flex items-center gap-1.5">
              <span className="font-semibold text-sm text-foreground whitespace-nowrap tracking-tight">
                GlobalAtlas
              </span>
              <span className="text-2xs px-1 py-0.5 rounded border border-primary/30 text-primary font-semibold leading-none">Beta</span>
            </div>
          )}
        </div>
      </div>

      {/* Nav Groups */}
      <nav className="flex-1 overflow-y-auto overflow-x-hidden py-4 space-y-1">
        {navGroups.map((group) => {
          const visibleItems = group.items.filter(item => !item.adminOnly || isAdmin);
          if (visibleItems.length === 0) return null;
          return (
            <div key={`group-${group.label}`} className="mb-4">
              {!collapsed && (
                <p className="px-4 mb-1 text-2xs font-semibold uppercase tracking-widest text-muted-foreground">
                  {group.label}
                </p>
              )}
              {visibleItems.map((item) => {
                const Icon = item.icon;
                const isActive = activeRoute === item.href;
                return (
                  <Link
                    key={item.id}
                    href={item.href}
                    title={collapsed ? item.label : undefined}
                    className={`flex items-center gap-3 px-4 py-2.5 mx-2 rounded-md text-sm font-medium transition-all duration-150 group relative
                      ${isActive ? 'sidebar-link-active' : 'sidebar-link-inactive hover:bg-muted hover:text-foreground'}`}
                  >
                    <Icon size={16} className="flex-shrink-0" />
                    {!collapsed && (
                      <span className="truncate flex-1">{item.label}</span>
                    )}
                    {!collapsed && item.badge && (
                      <span className={`text-2xs font-semibold px-1.5 py-0.5 rounded-full ${badgeClasses[item.badgeVariant || 'info']}`}>
                        {item.badge}
                      </span>
                    )}
                    {collapsed && item.badge && (
                      <span className={`absolute top-1 right-1 w-2 h-2 rounded-full ${item.badgeVariant === 'danger' ? 'bg-danger' : 'bg-warning'}`} />
                    )}
                  </Link>
                );
              })}
            </div>
          );
        })}
      </nav>

      {/* User & Collapse */}
      <div className="border-t border-border p-3 space-y-2 flex-shrink-0">
        {!collapsed && user && (
          <div
            className="flex items-center gap-2 px-2 py-2 rounded-md hover:bg-muted transition-colors cursor-pointer"
            onClick={logout}
            title="Sign out"
          >
            <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${isAdmin ? 'bg-primary/30' : 'bg-primary/20'}`}>
              <span className="text-primary text-xs font-semibold">{user.initials}</span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-medium text-foreground truncate">{user.name}</p>
              <p className="text-2xs text-muted-foreground truncate">
                {isAdmin ? '⚡ Admin · Full Access' : `${user.company} · ${user.role}`}
              </p>
            </div>
            <LogOut size={14} className="text-muted-foreground flex-shrink-0" />
          </div>
        )}
        {!collapsed && !user && (
          <Link
            href="/sign-up-login-screen"
            className="flex items-center gap-2 px-2 py-2 rounded-md hover:bg-muted transition-colors text-xs text-muted-foreground"
          >
            <LogOut size={14} />
            Sign In
          </Link>
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="w-full flex items-center justify-center p-2 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>
    </aside>
  );
}